import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { RiskAreaHighestRisk } from "../../risk/riskAreaRisksTile/RiskAreaRisks.types";
import { ListFilter, SortDirection, TextFilter } from "@common/api/filterTypes";
import { EntityFieldType } from "@common/entityField.types";
import { AmountRangeFilter, DateRangeFilter } from "../../contract/ContractFilter.types";
import { LevelOfProtectionType } from "../../legalEntity/addLegalEntityDialog/AddLegalEntityDialog.types";
import { ProcessingActivityColumn } from "./processingActivityColumns";

// Mirrors Core/Filtering/ProcessingActivities/ProcessingActivityFilterRequest.cs. searchText sits outside the saved
// view blob: search is deliberately not persisted with a view.
export interface ProcessingActivityFilterRequest {
    searchText?: string;
    showOnlyMine?: boolean;

    id?: ListFilter<string>;
    status?: ListFilter<ProcessingActivityStatus>;
    businessAreaIds?: ListFilter<string>;
    hasBusinessAreas?: boolean;
    dataCategoryIds?: ListFilter<string>;
    dataSubjectIds?: ListFilter<string>;
    purposeIds?: ListFilter<string>;
    systemIds?: ListFilter<string>;
    responsibleIds?: ListFilter<string>;
    groupEntityIds?: ListFilter<string>;

    // The filters behind the columns absorbed from the custom list.
    description?: TextFilter;
    approvalNumber?: TextFilter;
    policyIds?: ListFilter<string>;
    securityMeasureIds?: ListFilter<string>;
    legalBasisIds?: ListFilter<string>;
    nationalLawIds?: ListFilter<string>;
    deletionPeriodIds?: ListFilter<string>;
    tagIds?: ListFilter<string>;
    sharedResponsibleIds?: ListFilter<string>;
    validatedByIds?: ListFilter<string>;
    dataSources?: ListFilter<number>;
    numberOfDataSubjects?: ListFilter<number>;
    numberOfProcessors?: ListFilter<number>;
    customNumberOfDataSubjects?: AmountRangeFilter;
    customNumberOfProcessors?: AmountRangeFilter;
    validationDateRange?: DateRangeFilter;
    lastUpdatedRange?: DateRangeFilter;
    reportApprovalDateRange?: DateRangeFilter;
    reportDataProcessorEndDateRange?: DateRangeFilter;

    // Every count column takes an inclusive range.
    dataCategoryCount?: AmountRangeFilter;
    systemCount?: AmountRangeFilter;
    groupEntityCount?: AmountRangeFilter;
    sharingCount?: AmountRangeFilter;
    senderCount?: AmountRangeFilter;
    recipientCount?: AmountRangeFilter;
    recipientsInEuCount?: AmountRangeFilter;
    recipientsInEeaCount?: AmountRangeFilter;
    recipientsWithAdequateProtectionCount?: AmountRangeFilter;
    recipientsInInsecureThirdCountryCount?: AmountRangeFilter;
    dataProcessorSharingCount?: AmountRangeFilter;
    disclosureSharingCount?: AmountRangeFilter;
    sourceSharingCount?: AmountRangeFilter;
    dataControllerSharingCount?: AmountRangeFilter;
    jointDataControllerSharingCount?: AmountRangeFilter;
    documentCount?: AmountRangeFilter;

    transferBasisIds?: ListFilter<string>;
    dataProcessorIds?: ListFilter<string>;
    disclosureRecipientIds?: ListFilter<string>;
    sourceIds?: ListFilter<string>;
    dataControllerIds?: ListFilter<string>;
    jointDataControllerIds?: ListFilter<string>;
    recipientProtectionLevels?: ListFilter<LevelOfProtectionType>;
    documentTypeIds?: ListFilter<string>;

    // Only one applies, as the Risk column shows: the area levels under Enterprise Risk Management, else the score.
    riskAssessmentScore?: ListFilter<number>;
    riskAreaLevels?: ListFilter<string>;

    progress?: ListFilter<ProcessingActivityProgressStatus>;

    pageNumber?: number;
    pageSize?: number;
    sortByColumn?: ProcessingActivityColumn;
    sortDirection?: SortDirection;
}

// Mirrors ProcessingActivityTableRequest.cs. No custom fields: a displayed field is always a built-in column.
export interface ProcessingActivityFieldOrderItem {
    column: ProcessingActivityColumn;
    displayOrder: number;
}

export interface ProcessingActivitySearchRequest {
    filter: ProcessingActivityFilterRequest;
    displayFields: Array<ProcessingActivityFieldOrderItem>;
}

export interface ProcessingActivityExportHeader {
    header: string;
    column: ProcessingActivityColumn;
}

export interface ProcessingActivityFieldValue {
    value: any;
    type: EntityFieldType;
    column: ProcessingActivityColumn;
    referencedId?: string;
}

export interface ProcessingActivityProgressValue {
    progress: number;
    isValidationDue: boolean;
}

export interface ProcessingActivityFilterItem {
    id: string;
    name: string;
    status: ProcessingActivityStatus;
    // Whether the user can see every group entity on the activity, which validation requires.
    hasAccessToAllGroupEntities: boolean;
    fields: Array<ProcessingActivityFieldValue>;
}

export interface ProcessingActivitySearchResult {
    processingActivities: Array<ProcessingActivityFilterItem>;
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface ProcessingActivityOverViewModel {
    processingActivityOverviewRows: Array<ProcessingActivityOverViewRow>;
    dataCategoryItems: Array<DotLegalSelectOption>;
    dataSubjectItems: Array<DotLegalSelectOption>;
    businessAreaItems: Array<DotLegalSelectOption>;
    purposeItems: Array<DotLegalSelectOption>;
    currentRiskAssessmentVersionId: string | undefined;
    hasMultipleGroupCompanies: boolean;
}

export interface ProcessingActivityValidationDueViewModel {
    processingActivityId: string;
    isDue: boolean;
}

export interface ProcessingActivityOverViewRow {
    id: string;
    userFriendlyId: string;
    name: string;
    systemCount: number;
    processingCount: number;
    transferCount: number;
    sourceCount: number;
    progress: number;
    businessAreas: Array<DotLegalSelectOption>;
    legalEntityCount: number;
    dataCategoryIds: Array<string>;
    dataSubjectIds: Array<string>;
    businessAreaIds: Array<string>;
    purposeIds: Array<string>;
    riskAssessmentTotalScore: number | undefined;
    scenariosScore: number | undefined;
    riskAreas?: Array<RiskAreaHighestRisk>;
    processingActivityStatus: ProcessingActivityStatus;
    owner: string | undefined;
    lastStatusUpdate: Date;
    approvelNumber: string | undefined;
    canValidateProcessingActivity: boolean;
    sharedResponsibles: Array<string>;
}

export interface ProcessingActivityRow extends ProcessingActivityOverViewRow {
    sharedResponsibleString: string;
    riskNameString: string;
    classificationString: string;
    businessAreasString: string;
}

export interface ProcessingActivitiesProgressViewModel {
    overallProgress: number;
    rows: Array<ProcessingActivityProgressRow>;
}

export interface ProcessingActivityProgressRow {
    id: string;
    progressScore: number;
    canValidateProcessingActivity: boolean;
}

export interface ProcessingActivityRiskRow {
    id: string;
    riskAssessmentTotalScore: number | undefined;
    scenariosScore: number | undefined;
    riskAreas: Array<RiskAreaHighestRisk>;
}

export interface SelectedProcessingActivityReport {
    showDialog: boolean;
    processingActivityId: string;
    processingActivityName: string;
    processingActivityStatus: ProcessingActivityStatus;
}

export enum ProcessingActivityStatusEnum {
    notStarted = "0",
    inProgress = "1",
    completed = "2",
}

export enum ProcessingActivityStatus {
    Active = 0,
    Draft = 1,
    Archived = 2,
    DraftSubmitted = 3,
    AwaitingResearcher = 4,
    Cancelled = 5,
}

// Mirrors Core/Filtering/ProcessingActivities/ProcessingActivityProgressStatus.cs: the buckets the Progress filter
// offers over the percentage.
export enum ProcessingActivityProgressStatus {
    NotStarted = 0,
    InProgress = 1,
    Completed = 2,
}
